import React, { useState, useEffect } from "react";
import { useUser } from "../../context/UserContext";
import reportingAPI from "../../services/ReportingAPI";
import MemberViewAPI from "../../services/MemberViewAPI";
import { formatDateOnly } from "../../utils/DateTimeUtil";
import "../../css/Common.css";
import "../css/ShieldScoreWidget.css";

/**
 * Shield Score Widget Component
 * Displays the user's current Shield Score on the dashboard
 * with trend indicators and quick actions.
 *
 * @param {Function} setHeroContent - Function to navigate to different views
 */
function ShieldScoreWidget({ setHeroContent }) {
  const { loggedInUser, isReadOnlyView, selectedUser } = useUser();

  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isReadOnlyView && selectedUser?.uuid) {
      fetchShieldScore();
    } else if (!isReadOnlyView && loggedInUser?.uuid) {
      fetchShieldScore();
    }
  }, [loggedInUser, isReadOnlyView, selectedUser?.uuid]);

  const fetchShieldScore = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (isReadOnlyView && selectedUser?.uuid) {
        response = await MemberViewAPI.getMemberShieldScore(selectedUser.uuid);
      } else {
        response = await reportingAPI.getCurrentShieldScore();
      }
      setScoreData(response);
    } catch (err) {
      console.error("[ShieldScoreWidget] Error fetching score:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReports = () => {
    if (setHeroContent) {
      setHeroContent("Shield Reports");
    }
  };

  const handleGenerateReport = async () => {
    try {
      const response = await reportingAPI.generatePersonalReport({
        periodType: "WEEKLY",
        sendEmail: false,
      });

      if (response.success && setHeroContent) {
        // Navigate to reports page - the generated report will be at the top
        setHeroContent("Shield Reports");
      }
    } catch (err) {
      console.error("[ShieldScoreWidget] Error generating report:", err);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="dashboard-card shield-score-widget">
        <h4>🛡️ Your Weekly Shield Score</h4>
        <div className="shield-score-loading">
          <div className="shield-score-circle loading">
            <span className="score-value">--</span>
            <span className="score-label">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // No score available yet
  if (!scoreData?.hasScore) {
    return (
      <div className="dashboard-card shield-score-widget">
        <h4>🛡️ Your Weekly Shield Score</h4>
        <div className="shield-score-empty">
          <div className="shield-score-circle empty">
            <span className="score-icon">🔒</span>
            <span className="score-label">Not Yet Available</span>
          </div>
          <p className="shield-score-message">
            {scoreData?.message ||
              "Complete more training activities to unlock your Shield Score."}
          </p>
          {scoreData?.eligible && !isReadOnlyView && (
            <button className="shield-score-cta" onClick={handleGenerateReport}>
              Generate Your First Report
            </button>
          )}
        </div>
      </div>
    );
  }

  // Has score - render full widget
  const score = scoreData.shieldScore;
  const level = scoreData.scoreLevel;
  const trend = reportingAPI.formatTrend(
    scoreData.trendDirection,
    scoreData.trendPercentage || 0,
  );
  const scoreColor = reportingAPI.getScoreColor(score);

  // Encouraging level label for 50+ audience
  const getShieldLevelLabel = (lvl) => {
    if (lvl === "EXCELLENT" || lvl === "STRONG") return "Excellent";
    if (lvl === "GOOD") return "Good Progress";
    if (lvl === "FAIR") return "Building Up 🌱";
    if (lvl === "NEEDS_IMPROVEMENT" || lvl === "POOR")
      return "Good Start — Keep Going!";
    return reportingAPI.formatScoreLevel(lvl);
  };

  return (
    <div className="dashboard-card shield-score-widget">
      <h4>🛡️ Your Weekly Shield Score</h4>
      <p className="shield-score-subtitle">Based on last week's activity</p>

      {/* Main Score Circle */}
      <div className="dashboard-section-center">
        <div
          className="shield-score-circle"
          style={{ borderColor: scoreColor }}
        >
          <span className="score-value" style={{ color: scoreColor }}>
            {score}
          </span>
          <span className="score-label">{getShieldLevelLabel(level)}</span>
        </div>
      </div>

      {/* Trend Indicator */}
      {scoreData.trendDirection && scoreData.trendDirection !== "NEW" && (
        <div className="shield-score-trend" style={{ color: trend.color }}>
          <span className="trend-icon">{trend.icon}</span>
          <span className="trend-text">{trend.text}</span>
          <span className="trend-comparison">
            from {scoreData.previousScore}
          </span>
        </div>
      )}

      {scoreData.trendDirection === "NEW" && (
        <div className="shield-score-trend new">
          <span className="trend-icon">★</span>
          <span className="trend-text">First Report!</span>
        </div>
      )}

      {/* Component Breakdown */}
      {scoreData.components && (
        <div className="shield-score-components">
          <h5 className="dashboard-section-header">Score Breakdown</h5>
          <div className="component-grid">
            <div className="component-item">
              <span className="component-icon">📧</span>
              <span className="component-label">Simulations</span>
              <span className="component-score">
                {scoreData.components.simulation}
              </span>
            </div>
            <div className="component-item">
              <span className="component-icon">📚</span>
              <span className="component-label">Training</span>
              <span className="component-score">
                {scoreData.components.training}
              </span>
            </div>
            <div className="component-item">
              <span className="component-icon">🔍</span>
              <span className="component-label">Detection</span>
              <span className="component-score">
                {scoreData.components.scamDetection}
              </span>
            </div>
            <div className="component-item">
              <span className="component-icon">🎯</span>
              <span className="component-label">Engagement</span>
              <span className="component-score">
                {scoreData.components.engagement}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Period Info */}
      <div className="shield-score-period">
        <span className="period-label">
          {scoreData.periodType === "WEEKLY" ? "Weekly" : "Monthly"} Report
        </span>
        <span className="period-dates">
          {formatDateOnly(scoreData.periodStart, "short")}
          {" - "}
          {formatDateOnly(scoreData.periodEnd, "short")}
        </span>
      </div>

      {/* Action Button */}
      <div className="shield-score-actions">
        <button
          className="btn btn-primary btn-full"
          onClick={handleViewReports}
          disabled={isReadOnlyView}
          title={isReadOnlyView ? "Not available in read-only view" : undefined}
        >
          View Full Report
        </button>
      </div>
    </div>
  );
}

export default ShieldScoreWidget;
